import { Task, TaskHistoryEntry } from '../../../types';
import {
  isFieldDifferent,
  mergeChecklists,
  mergeHistory,
  mergeNotes,
} from '../../../utils/taskConflictResolver';

const SCALAR_TASK_KEYS: (keyof Task)[] = [
  'name',
  'description',
  'start',
  'end',
  'startTime',
  'endTime',
  'subtitle',
  'type',
  'completed',
  'completedAt',
  'status',
  'assignee',
  'estimatedHours',
  'loggedHours',
  'priority',
  'color',
  'category',
  'organization',
  'contactEmail',
  'contactPhone',
  'contactPerson',
  'contactName',
  'lastContactDate',
  'role',
  'goals',
  'socialCapital',
  'mood',
  'contactFunction',
  'sortOrder',
  'progress',
  'workspace',
  'tenantId',
  'parentId',
  'projectId',
  'projectIds',
  'contactId',
  'personKey',
  'unstacked',
  'archived',
  'recurrence',
  'isExpanded',
  'isCollapsed',
  'x',
  'y',
];

function getHistoryEntryKey(entry: TaskHistoryEntry): string {
  return entry.id || `${entry.timestamp}_${entry.field}_${entry.user}`;
}

/**
 * Reconstructs a partial baseline snapshot from unshared `TaskHistoryEntry` records
 * when an explicit `baseTask` snapshot is not available.
 */
export function deriveSyntheticBaselineFromHistory(
  localTask: Task,
  remoteTask: Task
): Partial<Task> | undefined {
  const localHistory = localTask.history || [];
  const remoteHistory = remoteTask.history || [];
  if (localHistory.length === 0 && remoteHistory.length === 0) {
    return undefined;
  }

  const localKeys = new Set(localHistory.map(getHistoryEntryKey));
  const remoteKeys = new Set(remoteHistory.map(getHistoryEntryKey));

  const unsharedEntries = [
    ...localHistory.filter((e) => !remoteKeys.has(getHistoryEntryKey(e))),
    ...remoteHistory.filter((e) => !localKeys.has(getHistoryEntryKey(e))),
  ].sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0));

  if (unsharedEntries.length === 0) {
    return undefined;
  }

  const syntheticBase: Record<string, unknown> = {};
  const recordedFields = new Set<string>();

  for (const entry of unsharedEntries) {
    if (entry.field && !recordedFields.has(entry.field) && 'oldValue' in entry) {
      syntheticBase[entry.field] = entry.oldValue;
      recordedFields.add(entry.field);
    }
  }

  return recordedFields.size > 0 ? (syntheticBase as Partial<Task>) : undefined;
}

/**
 * 3-way Observed-Remove Set (OR-Set) merge for task dependencies.
 * - Dependencies present in `baseDeps` are kept only if present in both `localDeps` and `remoteDeps`
 *   (respecting explicit removals on either side).
 * - Dependencies newly added in `localDeps` or `remoteDeps` (absent from `baseDeps`) are preserved.
 */
export function mergeDependenciesWithBaseline(
  baseDeps?: (string | number)[],
  localDeps?: (string | number)[],
  remoteDeps?: (string | number)[]
): (string | number)[] | undefined {
  if (!localDeps && !remoteDeps) return undefined;

  const loc = localDeps || [];
  const rem = remoteDeps || [];

  if (!baseDeps) {
    const unionSet = new Map<string, string | number>();
    for (const d of [...loc, ...rem]) {
      const key = String(d);
      if (!unionSet.has(key)) unionSet.set(key, d);
    }
    return Array.from(unionSet.values());
  }

  const baseKeys = new Set(baseDeps.map((d) => String(d)));
  const localKeys = new Set(loc.map((d) => String(d)));
  const remoteKeys = new Set(rem.map((d) => String(d)));

  const result = new Map<string, string | number>();
  for (const d of [...loc, ...rem]) {
    const key = String(d);
    if (result.has(key)) continue;

    if (baseKeys.has(key)) {
      if (localKeys.has(key) && remoteKeys.has(key)) {
        result.set(key, d);
      }
    } else {
      result.set(key, d);
    }
  }

  return Array.from(result.values());
}

/**
 * Pure 3-way field-level merge function (`mergeTaskFieldsWithBaseline`) that combines
 * concurrent edits from `localTask` and `remoteTask` against `baseTask` (or a history-derived
 * synthetic baseline) without losing non-conflicting field updates.
 */
export function mergeTaskFieldsWithBaseline(
  baseTask: Task | undefined,
  localTask: Task,
  remoteTask: Task
): Task {
  const effectiveBase: Partial<Task> | undefined =
    baseTask ?? deriveSyntheticBaselineFromHistory(localTask, remoteTask);

  const mergedRecord: Record<string, unknown> = { ...localTask };
  const localRecord = localTask as unknown as Record<string, unknown>;
  const remoteRecord = remoteTask as unknown as Record<string, unknown>;
  const baseRecord = effectiveBase as unknown as Record<string, unknown> | undefined;

  for (const key of SCALAR_TASK_KEYS) {
    const k = key as string;
    const localVal = localRecord[k];
    const remoteVal = remoteRecord[k];
    const hasBaseKey = Boolean(baseRecord && k in baseRecord);
    const baseVal = hasBaseKey ? baseRecord![k] : baseTask ? undefined : undefined;

    if (baseTask || hasBaseKey) {
      const localChanged = isFieldDifferent(localVal, baseVal);
      const remoteChanged = isFieldDifferent(remoteVal, baseVal);

      if (remoteChanged && !localChanged) {
        mergedRecord[k] = remoteVal;
      } else if (localChanged && !remoteChanged) {
        mergedRecord[k] = localVal;
      } else if (localChanged && remoteChanged) {
        mergedRecord[k] = localVal;
      } else if (remoteVal !== undefined && localVal === undefined) {
        mergedRecord[k] = remoteVal;
      }
    } else {
      const localDefined = isFieldDifferent(localVal, undefined);
      const remoteDefined = isFieldDifferent(remoteVal, undefined);
      if (!localDefined && remoteDefined) {
        mergedRecord[k] = remoteVal;
      } else {
        mergedRecord[k] = localVal;
      }
    }
  }

  // Coupled Invariant 1: status <-> completed <-> completedAt consistency
  const hasExplicitStatusBase = Boolean(
    baseTask || (baseRecord && ('status' in baseRecord || 'completed' in baseRecord))
  );
  if (hasExplicitStatusBase) {
    const baseStatus = baseRecord?.status;
    const baseCompleted = baseRecord?.completed;
    const localStatusChanged =
      isFieldDifferent(localTask.status, baseStatus) ||
      isFieldDifferent(localTask.completed, baseCompleted);
    const remoteStatusChanged =
      isFieldDifferent(remoteTask.status, baseStatus) ||
      isFieldDifferent(remoteTask.completed, baseCompleted);

    if (localStatusChanged && remoteStatusChanged) {
      // Both sides modified the status/completion state: keep local status group coherent
      mergedRecord.status = localTask.status;
      mergedRecord.completed =
        localTask.completed !== undefined
          ? localTask.completed
          : localTask.status === 'completed';
      mergedRecord.completedAt = localTask.completedAt;
    } else if (remoteStatusChanged && !localStatusChanged) {
      mergedRecord.status = remoteTask.status;
      mergedRecord.completed =
        remoteTask.completed !== undefined
          ? remoteTask.completed
          : remoteTask.status === 'completed';
      if (remoteTask.completedAt !== undefined) {
        mergedRecord.completedAt = remoteTask.completedAt;
      }
    } else if (localStatusChanged && !remoteStatusChanged) {
      mergedRecord.status = localTask.status;
      mergedRecord.completed =
        localTask.completed !== undefined
          ? localTask.completed
          : localTask.status === 'completed';
      if (localTask.completedAt !== undefined) {
        mergedRecord.completedAt = localTask.completedAt;
      }
    }
  }

  // Coupled Invariant 2: start <= end date range validity
  const mergedStart = typeof mergedRecord.start === 'string' ? mergedRecord.start.trim() : '';
  const mergedEnd = typeof mergedRecord.end === 'string' ? mergedRecord.end.trim() : '';
  if (mergedStart && mergedEnd && mergedStart > mergedEnd) {
    const baseStart = baseRecord?.start;
    const localStartChanged = isFieldDifferent(localTask.start, baseStart);
    if (localStartChanged) {
      mergedRecord.end =
        localTask.end && localTask.end >= mergedStart ? localTask.end : mergedStart;
    } else {
      mergedRecord.start =
        localTask.start && localTask.start <= mergedEnd ? localTask.start : mergedEnd;
    }
  }

  const merged = mergedRecord as unknown as Task;

  // 3-way OR-Set Dependencies merge
  if (localTask.dependencies || remoteTask.dependencies) {
    merged.dependencies = mergeDependenciesWithBaseline(
      baseTask?.dependencies,
      localTask.dependencies,
      remoteTask.dependencies
    );
  }

  // 3-way Notes merge
  merged.notes = mergeNotes(localTask.notes, remoteTask.notes, baseTask?.notes);

  // 3-way Checklists merge
  merged.checklist = mergeChecklists(
    localTask.checklist,
    remoteTask.checklist,
    baseTask?.checklist
  );
  merged.contactChecklist = mergeChecklists(
    localTask.contactChecklist,
    remoteTask.contactChecklist,
    baseTask?.contactChecklist
  );

  // Deduplicated chronological History merge
  merged.history = mergeHistory(localTask.history, remoteTask.history);

  // Timers: keep highest trackedTime, preserve active timerStartedAt
  merged.trackedTime = Math.max(localTask.trackedTime || 0, remoteTask.trackedTime || 0);
  if (localTask.timerStartedAt) {
    merged.timerStartedAt = localTask.timerStartedAt;
  } else if (remoteTask.timerStartedAt) {
    merged.timerStartedAt = remoteTask.timerStartedAt;
  }

  // Timestamps
  merged.createdAt =
    localTask.createdAt || remoteTask.createdAt || baseTask?.createdAt || Date.now();
  merged.updatedAt = Math.max(localTask.updatedAt || 0, remoteTask.updatedAt || 0, Date.now());

  return merged;
}

export const mergeTaskFieldsConflictFree = mergeTaskFieldsWithBaseline;
